import { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CompositeProductForm({ onClose, onSuccess, editProduct = null }) {
  const [ingredients, setIngredients] = useState([]);
  const [branches, setBranches] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    branch_id: '',
    type: 'composite',
    recipe: [],
    image_url: ''
  });
  
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchIngredients();
    fetchBranches();
    
    if (editProduct) {
      setFormData({
        name: editProduct.name,
        category: editProduct.category,
        branch_id: editProduct.branch_id || '',
        type: editProduct.type || 'composite',
        recipe: editProduct.recipe || [],
        image_url: editProduct.image_url || ''
      });
      
      // Set preview if product has image
      if (editProduct.image_url) {
        setImagePreview(`${API_URL}${editProduct.image_url}`);
      }
    }
  }, [editProduct]);

  const fetchIngredients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ingredients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setIngredients(data.ingredients || []);
    } catch (error) {
      console.error('Error fetching ingredients:', error);
    }
  };

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/branches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setBranches(data.branches || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const addIngredient = () => {
    const newRecipe = [...formData.recipe, {
      ingredient_id: '',
      name: '',
      quantity: 1,
      unit: 'pcs',
      price_per_unit: 0
    }];
    setFormData({...formData, recipe: newRecipe});
  };

  const removeIngredient = (index) => {
    const newRecipe = formData.recipe.filter((_, i) => i !== index);
    setFormData({...formData, recipe: newRecipe});
  };

  const updateRecipeItem = (index, field, value) => {
    const newRecipe = [...formData.recipe];
    newRecipe[index][field] = value;

    // If ingredient_id changes, auto-fill name, unit, price
    if (field === 'ingredient_id') {
      const ingredient = ingredients.find(ing => ing.id === value);
      if (ingredient) {
        newRecipe[index].name = ingredient.name;
        newRecipe[index].unit = ingredient.unit;
        newRecipe[index].price_per_unit = ingredient.price_per_unit;
      }
    }

    setFormData({...formData, recipe: newRecipe});
  };

  const calculateBasePrice = () => {
    return formData.recipe.reduce((total, item) => {
      return total + (item.quantity * item.price_per_unit);
    }, 0);
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;
    
    try {
      setUploadingImage(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/products/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });
      
      const data = await response.json();
      
      if (data.success) {
        return data.image_url;
      }
      
      return null;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Gagal upload gambar');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };
  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 5MB');
        return;
      }
      
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Format file harus JPEG, PNG, atau WebP');
        return;
      }
      
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.branch_id) {
      toast.error('Pilih cabang terlebih dahulu!', {
        description: 'Cabang harus dipilih untuk produk komposit',
        duration: 3000
      });
      return;
    }

    if (formData.recipe.length === 0) {
      toast.error('Tambahkan minimal 1 bahan untuk produk komposit!');
      return;
    }

    // Upload image first if there's a new image
    let imageUrl = formData.image_url; // Keep existing image URL
    if (imageFile) {
      const uploadedUrl = await handleImageUpload(imageFile);
      if (uploadedUrl) {
        imageUrl = uploadedUrl;
      }
    }

    const basePrice = calculateBasePrice();
    const productData = {
      ...formData,
      price: basePrice,
      image_url: imageUrl
    };

    try {
      const token = localStorage.getItem('token');
      const url = editProduct
        ? `${API_URL}/api/products/${editProduct.id}`
        : `${API_URL}/api/products`;
      
      const method = editProduct ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(productData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(editProduct ? 'Produk berhasil diupdate! Refresh halaman untuk melihat perubahan.' : 'Produk komposit berhasil dibuat! Refresh halaman untuk melihat produk baru.');
        onSuccess();
        onClose();
      } else {
        const data = await response.json();
        toast.error(`Error: ${data.detail || 'Gagal menyimpan produk'}`);
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Gagal menyimpan produk');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">
            {editProduct ? 'Edit Produk Komposit' : 'Buat Produk Komposit'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nama Produk *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategori *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Pilih Kategori</option>
                <option value="makanan">Makanan</option>
                <option value="minuman">Minuman</option>
                <option value="snack">Snack</option>
              </select>
            </div>
          </div>

          {/* Branch Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cabang *
            </label>
            <select
              value={formData.branch_id}
              onChange={(e) => setFormData({...formData, branch_id: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            >
              <option value="">Pilih Cabang</option>
              {branches.map((branch) => (
                <option key={branch.branch_id} value={branch.branch_id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
               Produk akan tersedia di cabang yang dipilih
            </p>
          </div>

          {/* Recipe */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Resep / Komposisi
              </label>
              <button
                type="button"
                onClick={addIngredient}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
              >
                <Plus className="h-4 w-4" />
                Tambah Bahan
              </button>
            </div>

            <div className="space-y-3">
              {formData.recipe.map((item, index) => (
                <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-md">
                  <div className="flex-1 grid grid-cols-3 gap-2">
                    <select
                      value={item.ingredient_id}
                      onChange={(e) => updateRecipeItem(index, 'ingredient_id', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Pilih Bahan</option>
                      {ingredients.map(ing => (
                        <option key={ing.id} value={ing.id}>
                          {ing.name} ({ing.stock} {ing.unit})
                        </option>
                      ))}
                    </select>

                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateRecipeItem(index, 'quantity', parseInt(e.target.value))}
                      placeholder="Jumlah"
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      min="1"
                      required
                    />

                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {item.unit} @ Rp {item.price_per_unit.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeIngredient(index)}
                    className="text-red-600 hover:text-red-800 p-2"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              ))}

              {formData.recipe.length === 0 && (
                <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-md">
                  Belum ada bahan. Klik "Tambah Bahan" untuk menambahkan.
                </div>
              )}
            </div>
          </div>

          {/* Image Upload Section */}
          <div className="mb-6 border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Foto Produk (Opsional)
            </label>
            <div className="flex items-start gap-4">
              {/* Image Preview */}
              {imagePreview && (
                <div className="flex-shrink-0">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-24 h-24 object-cover rounded-lg border-2 border-gray-300"
                  />
                </div>
              )}
              
              {/* Upload Input */}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  disabled={uploadingImage}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Format: JPEG, PNG, WebP. Maksimal 5MB.
                </p>
                {uploadingImage && (
                  <p className="text-xs text-blue-600 mt-1">
                    Mengupload gambar...
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Price Summary */}
          {formData.recipe.length > 0 && (
            <div className="bg-blue-50 p-4 rounded-md mb-6">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Harga Dasar (dihitung otomatis):</span>
                <span className="text-xl font-bold text-blue-600">
                  Rp {calculateBasePrice().toLocaleString()}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                *Harga akan disesuaikan saat customer customize jumlah bahan
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 justify-end pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Batal
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {editProduct ? 'Update Produk' : 'Buat Produk'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
